import { useCallback, useState } from "react";
import {
  acknowledgeETaxItem,
  holdETaxDocument,
  releaseETaxDocumentHold,
} from "../../services";
import { ETaxDocument } from "../../types/e-tax.type";

export type AutoSendNoteAction = "HOLD" | "ACKNOWLEDGE";

export interface AutoSendNoteTarget {
  action: AutoSendNoteAction;
  doc: ETaxDocument;
}

/**
 * Row actions for the weekly auto-send: hold a bill out of the round, release the hold, and
 * acknowledge a failure. Hold and acknowledge go through a note dialog; release is immediate
 * because it only puts the bill back where it was.
 *
 * Failures are reported by the API error toast, and the dialog stays open so the bill it
 * names is still on screen. The reload is the caller's own fetch, so the list (and anything
 * else the caller counts) repaints after every change.
 */
export function useEtaxAutoSendActions(reload: () => void) {
  const [noteTarget, setNoteTarget] = useState<AutoSendNoteTarget | null>(null);
  const [submitting, setSubmitting] = useState<boolean>(false);
  const [releasingId, setReleasingId] = useState<string | null>(null);

  const askHold = useCallback(
    (doc: ETaxDocument) => setNoteTarget({ action: "HOLD", doc }),
    [],
  );

  const askAcknowledge = useCallback(
    (doc: ETaxDocument) => setNoteTarget({ action: "ACKNOWLEDGE", doc }),
    [],
  );

  const closeNote = useCallback(() => {
    if (!submitting) setNoteTarget(null);
  }, [submitting]);

  const confirmNote = useCallback(
    async (note: string | null) => {
      if (!noteTarget || submitting) return;
      const { action, doc } = noteTarget;

      setSubmitting(true);
      try {
        if (action === "HOLD") {
          await holdETaxDocument(doc.transaction_uuid, note);
        } else {
          if (!doc.etax_uuid) return;
          await acknowledgeETaxItem(doc.etax_uuid, note);
        }
        setNoteTarget(null);
        reload();
      } catch {
        // Surfaced by the axios interceptor.
      } finally {
        setSubmitting(false);
      }
    },
    [noteTarget, submitting, reload],
  );

  const releaseHold = useCallback(
    async (doc: ETaxDocument) => {
      if (releasingId) return;
      setReleasingId(doc.transaction_uuid);
      try {
        await releaseETaxDocumentHold(doc.transaction_uuid);
        reload();
      } catch {
        // Surfaced by the axios interceptor.
      } finally {
        setReleasingId(null);
      }
    },
    [releasingId, reload],
  );

  return {
    noteTarget,
    noteSubmitting: submitting,
    askHold,
    askAcknowledge,
    closeNote,
    confirmNote,
    releasingId,
    releaseHold,
  };
}
